import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { ClientsIcon, DeliveryIcon } from '../Layout/icons';
import { useAuth } from '../../context/AuthContext';
import { useCustomerSelector } from '../../context/CustomerSelectorContext';
import {
    REPORTS_ACCESS_ROLES,
    TRANSPORT_COMPANY_REPORT_ROLES,
} from '../../constants/roles';
import { REPORT_MAX_PERIOD_DAYS_KEY, parseConfigurationInteger } from '../../constants/systemConfiguration';
import { ROUTES } from '../../routes';
import configurationService from '../../services/configurationService';
import { deliverReportFile, emitReport } from '../../services/reportService';
import { MAX_DELIVERY_DATE_RANGE_DAYS } from '../../utils/deliveryDateRange';
import { getReportsForRoles } from './reportsCatalog';
import { buildReportParameters } from './reportFilterUtils';
import ReportFiltersModal from './ReportFiltersModal';
import {
    Page,
    Title,
    Subtitle,
    PeriodLimit,
    CardsGrid,
    ReportCard,
    CardIcon,
    CardTitle,
    CardDescription,
} from './styles';

const REPORT_ICONS = {
    'delivery-listing': DeliveryIcon,
    'delivery-listing-by-company': ClientsIcon,
};

export default function Relatorios() {
    const { user } = useAuth();
    const { selectedCustomerId, selectedCustomerName } = useCustomerSelector();
    const [selectedReportId, setSelectedReportId] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [periodLimit, setPeriodLimit] = useState({ status: 'loading', days: null });

    const userRoles = user?.roles;

    const canAccessReports = useMemo(
        () => (userRoles || []).some((role) => REPORTS_ACCESS_ROLES.includes(role)),
        [userRoles]
    );

    useEffect(() => {
        if (!canAccessReports) return undefined;
        let active = true;
        configurationService.getConfiguration(REPORT_MAX_PERIOD_DAYS_KEY)
            .then((config) => {
                if (!active) return;
                const days = parseConfigurationInteger(config?.value);
                if (days == null || days < 1) {
                    setPeriodLimit({ status: 'error', days: null });
                    return;
                }
                setPeriodLimit({ status: 'ready', days });
            })
            .catch(() => {
                if (!active) return;
                setPeriodLimit({ status: 'error', days: null });
            });
        return () => {
            active = false;
        };
    }, [canAccessReports]);

    const maxPeriodDays = periodLimit.status === 'ready'
        ? periodLimit.days
        : periodLimit.status === 'error'
            ? MAX_DELIVERY_DATE_RANGE_DAYS
            : undefined;

    const reports = useMemo(
        () => getReportsForRoles(userRoles || [], maxPeriodDays),
        [userRoles, maxPeriodDays],
    );

    const selectedReport = useMemo(
        () => reports.find((report) => report.id === selectedReportId) ?? null,
        [reports, selectedReportId],
    );

    /**
     * O cliente (ROLE_LEGAL_PERSON) não escolhe cliente — o backend usa o customer do
     * token e o filtro fica oculto. Mesmo assim o id da própria empresa é necessário
     * para listar os centros de custo; ele não vai nos `parameters` porque o campo
     * não faz parte do schema resolvido para essa role.
     */
    const ownCustomerValues = useMemo(() => {
        const roles = userRoles || [];
        const isTransportCompanyUser = TRANSPORT_COMPANY_REPORT_ROLES.some((role) =>
            roles.includes(role)
        );
        if (isTransportCompanyUser || !selectedCustomerId) return undefined;
        return { customerId: selectedCustomerId, customerName: selectedCustomerName };
    }, [userRoles, selectedCustomerId, selectedCustomerName]);

    if (!canAccessReports) {
        return <Navigate to={ROUTES.DASHBOARD} replace />;
    }

    const handleCloseModal = () => {
        if (submitting) return;
        setSelectedReportId(null);
        setModalError(null);
    };

    const handleGenerate = async (values) => {
        const report = selectedReport;
        if (!report) return;

        setSubmitting(true);
        setModalError(null);

        try {
            const parameters = buildReportParameters(values, { filters: report.filters });
            const file = await emitReport({
                type: report.type,
                format: values.format,
                parameters,
            });
            deliverReportFile(file);
            setSelectedReportId(null);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            setModalError(err?.message || 'Erro ao gerar o relatório.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Page>
            <div>
                <Title>Relatórios</Title>
                <Subtitle>Selecione um relatório para informar os filtros e gerar o arquivo.</Subtitle>
                <PeriodLimit>
                    {periodLimit.status === 'ready'
                        ? `O intervalo entre as datas selecionadas não pode exceder ${periodLimit.days} dias.`
                        : periodLimit.status === 'error'
                            ? `Não foi possível consultar o limite de dias. Enquanto isso, o intervalo não pode exceder ${MAX_DELIVERY_DATE_RANGE_DAYS} dias.`
                            : 'Consultando o limite de dias do período...'}
                </PeriodLimit>
            </div>

            <CardsGrid>
                {reports.map((report) => {
                    const Icon = REPORT_ICONS[report.id];
                    return (
                        <ReportCard
                            key={report.id}
                            type="button"
                            onClick={() => {
                                setModalError(null);
                                setSelectedReportId(report.id);
                            }}
                        >
                            {Icon ? (
                                <CardIcon>
                                    <Icon />
                                </CardIcon>
                            ) : null}
                            <CardTitle>{report.title}</CardTitle>
                            <CardDescription>{report.description}</CardDescription>
                        </ReportCard>
                    );
                })}
            </CardsGrid>

            <ReportFiltersModal
                open={Boolean(selectedReport)}
                title={selectedReport?.title || 'Filtros'}
                description="Informe os filtros desejados para gerar o relatório."
                filters={selectedReport?.filters}
                initialValues={ownCustomerValues}
                submitting={submitting}
                error={modalError}
                maxPeriodDays={maxPeriodDays}
                onClose={handleCloseModal}
                onSubmit={handleGenerate}
            />
        </Page>
    );
}
